import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Book } from "lucide-react";
import { authAxios } from "../utils/auth";

export default function OpsRunbookPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [html, setHtml] = useState(null);
  const [error, setError] = useState(null);
  const section = searchParams.get("section");

  useEffect(() => {
    authAxios.get("/analytics/command/health/runbook")
      .then(res => setHtml(res.data?.html || ""))
      .catch(e => setError(e?.response?.data?.detail || e.message));
  }, []);

  useEffect(() => {
    if (!html || !section) return;
    // Scroll to the requested anchor once markdown has rendered
    const t = setTimeout(() => {
      const el = document.getElementById(section);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
    return () => clearTimeout(t);
  }, [html, section]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white/80" data-testid="ops-runbook-page">
      <header className="sticky top-0 z-10 border-b border-white/[0.06] bg-[#0a0a0a]/95 backdrop-blur">
        <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 rounded-lg hover:bg-white/[0.05] text-white/60"
            data-testid="runbook-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Book className="w-5 h-5 text-[#C5A55A]" />
          <div>
            <div className="text-sm font-semibold text-white/90">Ops Runbook</div>
            <div className="text-[11px] text-white/40">Section 16 · Agent Eros ARCHITECTURE.md</div>
          </div>
        </div>
      </header>

      <main className="max-w-[1000px] mx-auto px-4 sm:px-6 py-8">
        {error && (
          <div className="rounded-xl border border-red-500/20 bg-red-500/[0.03] p-4 text-red-400 text-sm" data-testid="runbook-error">
            {error}
          </div>
        )}
        {!error && html === null && (
          <div className="text-white/30 text-sm">Loading runbook...</div>
        )}
        {html !== null && (
          <article
            className="runbook-prose"
            data-testid="runbook-content"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        )}
      </main>

      <style>{`
        .runbook-prose { color: rgba(255,255,255,0.78); font-size: 14px; line-height: 1.7; }
        .runbook-prose h1 { font-size: 22px; color: #C5A55A; margin: 32px 0 12px; font-weight: 600; }
        .runbook-prose h2 { font-size: 18px; color: #C5A55A; margin: 28px 0 10px; font-weight: 600; padding-top: 12px; border-top: 1px solid rgba(255,255,255,0.06); }
        .runbook-prose h3 { font-size: 15px; color: rgba(255,255,255,0.9); margin: 24px 0 8px; font-weight: 600; scroll-margin-top: 80px; }
        .runbook-prose h4 { font-size: 13px; color: rgba(255,255,255,0.8); margin: 16px 0 6px; font-weight: 600; }
        .runbook-prose p { margin: 8px 0; }
        .runbook-prose ol, .runbook-prose ul { padding-left: 24px; margin: 8px 0; }
        .runbook-prose li { margin: 4px 0; }
        .runbook-prose code { background: rgba(197,165,90,0.08); color: #e8d5a3; padding: 1px 6px; border-radius: 4px; font-size: 12px; font-family: ui-monospace, monospace; }
        .runbook-prose pre { background: #111113; border: 1px solid rgba(255,255,255,0.06); padding: 12px; border-radius: 8px; overflow-x: auto; font-size: 12px; margin: 10px 0; }
        .runbook-prose pre code { background: transparent; padding: 0; color: rgba(255,255,255,0.85); }
        .runbook-prose a { color: #C5A55A; text-decoration: none; border-bottom: 1px dotted rgba(197,165,90,0.4); }
        .runbook-prose a:hover { border-bottom-style: solid; }
        .runbook-prose strong { color: rgba(255,255,255,0.95); font-weight: 600; }
        .runbook-prose table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 12px; }
        .runbook-prose th, .runbook-prose td { border: 1px solid rgba(255,255,255,0.08); padding: 6px 10px; text-align: left; }
        .runbook-prose th { background: rgba(255,255,255,0.03); color: rgba(255,255,255,0.85); font-weight: 600; }
        .runbook-prose :target { background: rgba(197,165,90,0.08); padding: 4px 8px; margin: 0 -8px; border-radius: 6px; }
      `}</style>
    </div>
  );
}
